import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useLang } from '../../context/LangContext';

const LOGO_URL = process.env.PUBLIC_URL + "/logo.png";

const navLinks = [
  { key: "nav.agentia", path: "/agent" },
  { key: "nav.sectors", path: "/secteurs" },
  { key: "nav.pricing", path: "/tarifs" },
  { key: "nav.contact", path: "/contact" },
];

export const Navbar = () => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <>
      <motion.nav
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-[#020209]/90 backdrop-blur-xl border-b border-white/[0.05]'
            : 'bg-transparent'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
            <img src={LOGO_URL} alt="AIpreneur" className="h-9 md:h-10" />
          </Link>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.slice(1)}`}
                className={`text-sm font-medium transition-colors duration-200 ${
                  location.pathname === link.path
                    ? 'text-white'
                    : 'text-neutral-400 hover:text-white'
                }`}
              >
                {t(link.key)}
              </Link>
            ))}
          </div>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-4">
            <button
              data-testid="lang-toggle"
              onClick={() => setLang(lang === 'fr' ? 'en' : 'fr')}
              className="flex items-center gap-2 text-xs font-medium tracking-wider px-3 py-1.5 rounded-full border border-white/[0.1] bg-white/[0.04] text-neutral-400 hover:text-white hover:border-white/[0.2] transition-all"
            >
              {lang === 'fr' ? (
                <>
                  <span className="text-base">🇬🇧</span>
                  <span>EN</span>
                </>
              ) : (
                <>
                  <span className="text-base">🇫🇷</span>
                  <span>FR</span>
                </>
              )}
            </button>
          </div>

          {/* Mobile hamburger */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-white p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </motion.nav>

      {/* Mobile menu overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-40 bg-[#020209]/98 backdrop-blur-xl flex flex-col items-center justify-center gap-8"
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            {navLinks.map((link, i) => (
              <motion.div
                key={link.path}
                initial={{ opacity: 0, x: -40 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 + 0.1 }}
              >
                <Link
                  to={link.path}
                  className="text-3xl font-bold text-white hover:text-brand-green transition-colors"
                  data-testid={`mobile-nav-${link.path.slice(1)}`}
                >
                  {t(link.key)}
                </Link>
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="flex items-center gap-4 mt-4"
            >
              <button
                onClick={() => setLang(lang === 'fr' ? 'en' : 'fr')}
                className="flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full border border-white/[0.15] text-neutral-400 hover:text-white transition-all"
              >
                {lang === 'fr' ? (
                  <>
                    <span className="text-lg">🇬🇧</span>
                    <span>EN</span>
                  </>
                ) : (
                  <>
                    <span className="text-lg">🇫🇷</span>
                    <span>FR</span>
                  </>
                )}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
